import React, { useState, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import ActionsModal from 'components/ActionsModal/ActionsModal'
import MultiSelect from 'react-multi-select-component'
import DropdownInput from 'components/DropdownInput/DropdownInput'
import { selectDisplayedZone, updateDisplayedZone, closeDisplayedZone } from 'store/organizations'
import messages from './messages'
import { selectVerticals, selectAllGroups, fetchGroups } from 'store/global'
import styles from './ZoneModal.scss'

const allowedModes = ['create', 'edit', 'delete', 'view'] as const
type Mode = typeof allowedModes[number]

interface Group {
  id: string
  path: string
  subGroups?: Group[]
}

interface Vertical {
  id: string
  name: string
}

interface GroupOption {
  value: string
  label: string
  path: string
}

interface ZoneModalProps {
  mode: Mode
  onSubmit: (zoneData: any) => void | Promise<void>
}

function formatZoneName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/\s+/g, '_')
}

function toSnakeCase(camelCaseString: string): string {
  return camelCaseString
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
}

export default function ZoneModal({ mode, onSubmit }: ZoneModalProps) {
  const zone = useSelector(selectDisplayedZone)
  const verticals = useSelector(selectVerticals)
  const allGroups = useSelector(selectAllGroups)
  const dispatch = useDispatch()

  const [selectedVerticals, setSelectedVerticals] = useState<string[]>(Object.keys(zone?.verticals || {}))
  const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([])

  useEffect(() => {
    if (!allGroups || allGroups.length === 0) {
      dispatch(fetchGroups())
    }
  }, [dispatch, allGroups])

  useEffect(() => {
    if (zone?.groups) {
      const findGroupByPath = (groups: Group[], targetPath: string): Group | null => {
        for (const group of groups || []) {
          if (group.path === targetPath) {
            return group
          }
          if (group.subGroups && group.subGroups.length > 0) {
            const found = findGroupByPath(group.subGroups, targetPath)
            if (found) return found
          }
        }
        return null
      }

      const groupOptions = zone.groups
        .map((groupPath: string) => {
          const group = findGroupByPath(allGroups, groupPath)
          return group ? { value: group.id, label: group.path } : null
        })
        .filter((option): option is GroupOption => option !== null)
      setSelectedGroups(groupOptions)
    } else {
      setSelectedGroups([])
    }
  }, [zone?.groups, allGroups])

  const flattenGroupsForOptions = (groups: Group[], level = 0): GroupOption[] => {
    let allGroups: GroupOption[] = []
    groups.forEach(group => {
      allGroups.push({
        value: group.id,
        label: group.path,
        path: group.path,
      })
      if (group.subGroups && group.subGroups.length > 0) {
        allGroups = allGroups.concat(flattenGroupsForOptions(group.subGroups, level + 1))
      }
    })
    return allGroups
  }

  const groupOptions = useMemo(() => {
    return flattenGroupsForOptions(allGroups || [])
  }, [allGroups])

  const onFieldChange = (key: string, value: any) => {
    if (key === 'verticals') {
      setSelectedVerticals(value.map((item: { value: string }) => item.value))
    }
    dispatch(updateDisplayedZone({ key, value }))
  }

  const onGroupsChange = (nextGroupsSelections: GroupOption[]) => {
    setSelectedGroups(nextGroupsSelections)
  }

  const getVerticalsOptions = (): Array<{ label: string; value: string }> => {
    return verticals.map((vertical: Vertical) => ({
      label: vertical.name,
      value: vertical.id,
    }))
  }

  const getVerticalsValue = (): Array<{ label: string; value: string }> => {
    return selectedVerticals.map(key => ({
      label: verticals.find((v: Vertical) => v.id === key)?.name || key,
      value: key,
    }))
  }

  const onVerticalsChange = (data: Array<{ label: string; value: string }>) => {
    const selectedKeys = data.map(item => item.value)
    setSelectedVerticals(selectedKeys)
  }

  const onSaveModal = () => {
    const updatedVerticals = { ...(zone?.verticals || {}) }

    selectedVerticals.forEach(key => {
      if (!Object.keys(updatedVerticals).includes(key)) {
        updatedVerticals[key] = {
          baseIndex: `${formatZoneName(zone?.name || '')}_${toSnakeCase(key)}_index`,
          activityIndex: `${formatZoneName(zone?.name || '')}_${toSnakeCase(key)}_activity_index`,
        }
      }
    })

    Object.keys(updatedVerticals).forEach(key => {
      if (!selectedVerticals.includes(key)) {
        delete updatedVerticals[key]
      }
    })

    const updatedGroups = selectedGroups.map(groupOption => groupOption.label)

    const zoneData = {
      ...zone,
      verticals: { ...updatedVerticals },
      groups: updatedGroups,
    }

    Object.keys(zoneData).forEach(key => {
      if (zoneData[key as keyof typeof zoneData] === undefined || zoneData[key as keyof typeof zoneData] === null) {
        delete zoneData[key as keyof typeof zoneData]
      }
    })

    dispatch(updateDisplayedZone({ key: 'verticals', value: updatedVerticals }))
    dispatch(updateDisplayedZone({ key: 'groups', value: updatedGroups }))
    onSubmit(zoneData)
  }

  const onCloseModal = () => dispatch(closeDisplayedZone())

  const fields = {
    name: {
      key: 'name',
      isRequired: true,
      validator: (value: string) => value.length > 0,
      label: <FormattedMessage {...messages.labels.name} />,
    },
    groups: {
      key: 'groups',
      isRequired: false,
      label: <FormattedMessage {...messages.labels.groups} />,
      renderInput: () => {
        if (mode === 'view') {
          const groups = zone?.groups || []
          if (groups.length === 0) {
            return (
              <div className={styles.readOnlyField}>
                No groups assigned
              </div>
            )
          }

          const shortGroupNames = groups.map((group: string) => {
            const parts = group.split('/')
            return parts[parts.length - 1]
          })
          const displayText = shortGroupNames.join(', ')
          const fullText = groups.join(', ')

          return (
            <div
              className={styles.readOnlyField}
              title={fullText}
            >
              {displayText}
            </div>
          )
        }

        return (
          <div>
            <DropdownInput
              options={groupOptions}
              selections={selectedGroups}
              onChange={onGroupsChange}
              hasSelectAll={false}
              disableSearch={false}
              dropdownHeader="Select groups..."
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Selected: {selectedGroups.length} groups
            </div>
          </div>
        )
      },
    },
    verticals: {
      key: 'verticals',
      isRequired: false,
      type: 'select',
      label: <FormattedMessage {...messages.labels.verticals} />,
      renderInput: () => (
        <MultiSelect
          disableSearch
          hasSelectAll={false}
          options={getVerticalsOptions()}
          value={getVerticalsValue()}
          onChange={onVerticalsChange}
        />
      ),
    },
  }

  return (
    <ActionsModal
      type='zone'
      mode={mode}
      item={zone}
      primaryKey='name'
      fields={fields}
      onFieldChange={onFieldChange}
      onClose={onCloseModal}
      onSubmit={onSaveModal}
    />
  )
}

